import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { CopyButton } from "@aurumiq/ui/composite/copy-button";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmy, money } from "@aurumiq/ui/lib/format";
import { ExternalLink } from "lucide-react";
import { QueryState, Stat, StatList } from "@/components/query-state";
import { useOrg, usePlans } from "./queries";

const STRIPE = "https://dashboard.stripe.com";

function StripeId({ id, kind }: { id: string | null; kind: "customers" | "subscriptions" }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="font-mono text-xs">{id}</span>
      <CopyButton value={id} variant="ghost" />
      <Button asChild variant="link" size="sm" className="h-auto px-0">
        <a href={`${STRIPE}/${kind}/${id}`} target="_blank" rel="noreferrer">
          Open in Stripe <ExternalLink />
        </a>
      </Button>
    </span>
  );
}

export function BillingTab({ orgId }: { orgId: string }) {
  const org = useOrg(orgId);
  const plans = usePlans();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Stripe</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryState query={org}>
            {(o) => (
              <StatList>
                <Stat label="Customer">
                  <StripeId id={o.stripe_customer_id} kind="customers" />
                </Stat>
                <Stat label="Subscription">
                  <StripeId
                    id={o.subscription?.stripe_subscription_id ?? null}
                    kind="subscriptions"
                  />
                </Stat>
                <Stat label="Status">
                  {o.subscription ? (
                    <StatusTag variant={statusVariant(o.subscription.status)}>
                      {o.subscription.status.toUpperCase()}
                    </StatusTag>
                  ) : (
                    "—"
                  )}
                </Stat>
                <Stat label="Period end">
                  {o.subscription?.current_period_end
                    ? dmy(o.subscription.current_period_end)
                    : "—"}
                </Stat>
              </StatList>
            )}
          </QueryState>
          <p className="mt-4 rounded-lg border border-dashed p-3 text-muted-foreground text-sm">
            Self-serve billing (customer portal, invoices, card updates) lands with the Stripe
            integration. Until then, change plans and seats from the Licenses tab; Stripe stays the
            source of truth for payments.
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Plan pricing</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryState query={plans}>
            {(p) =>
              p.fallback ? (
                <p className="text-muted-foreground text-sm">
                  Pricing is unavailable (billing plans endpoint not implemented yet).
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="py-1 pr-2 font-semibold">Plan</th>
                      <th className="py-1 pr-2 text-right font-semibold">Seats</th>
                      <th className="py-1 pr-2 text-right font-semibold">Monthly</th>
                      <th className="py-1 text-right font-semibold">Yearly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.items.map((plan) => (
                      <tr
                        key={plan.code}
                        className={
                          plan.code === org.data?.plan_code
                            ? "bg-gold-bg/40 font-semibold"
                            : undefined
                        }
                      >
                        <td className="py-1.5 pr-2">
                          {plan.name}
                          {plan.features.length ? (
                            <div className="font-normal text-muted-foreground text-xs">
                              {plan.features.join(" · ")}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular">{plan.seats}</td>
                        <td className="py-1.5 pr-2 text-right tabular">
                          {money(plan.price_month, plan.currency, 0)}
                        </td>
                        <td className="py-1.5 text-right tabular">
                          {money(plan.price_year, plan.currency, 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </QueryState>
        </CardContent>
      </Card>
    </div>
  );
}
